import { Routes as BrowserRoutes, Navigate, Outlet, Route } from 'react-router'
import { AiBridgeDetails } from '@/components/aiBridge/AiBridgeDetails/AiBridgeDetails'
import { AiBridgeListPage } from '@/components/aiBridge/AiBridgeListPage'
import { ApplicationsPage } from '@/components/applications/ApplicationsPage'
import { CameraDetails } from '@/components/cameras/CameraDetails/CameraDetails'
import { CameraListPage } from '@/components/cameras/CameraListPage'
import { CrossCameraInstallationPage } from '@/components/cross-camera/CrossCameraInstallationPage'
import { CrossCameraPage } from '@/components/cross-camera/CrossCameraPage'
import { CrossCameraPeoplePage } from '@/components/cross-camera/CrossCameraPeoplePage'
import { CrossCameraPipelinesPage } from '@/components/cross-camera/CrossCameraPipelinesPage'
import { DatasetDetailsPage } from '@/components/datasets/DatasetDetailsPage'
import { DatasetListPage } from '@/components/datasets/DatasetListPage'
import { LabelDataPage } from '@/components/datasets/LabelDataPage'
import { SelectDatasetPage } from '@/components/datasets/SelectDatasetPage'
import { DeploymentEditorPage } from '@/components/deployments/deployment-editor/DeploymentEditorPage'
import { DeploymentsPage } from '@/components/deployments/deployment-list/DeploymentsPage'
import { DeviceActivationPage } from '@/components/devices/DeviceActivationPage/DeviceActivationPage'
import { DeviceDetailsPage } from '@/components/devices/DeviceDetailsPage'
import { DeviceListPage } from '@/components/devices/DeviceListPage'
import { EventsPage } from '@/components/events/EventsPage'
import {
  AppDetailsLayout,
  AppRouteProvider
} from '@/components/layout/AppDetailsLayout/AppDetailsLayout'
import { HomeLayout } from '@/components/layout/HomeLayout/HomeLayout'
import { ModelDetailsLayout } from '@/components/layout/ModelDetailsLayout/ModelDetailsLayout'
import { PageContainer } from '@/components/layout/PageContainer/PageContainer'
import { OauthRedirect } from '@/components/login/sso/OauthRedirect'
import { LibraryModelListHandler } from '@/components/models/LibraryModelList/LibraryModelListHandler'
import { ModelDetailsPage } from '@/components/models/ModelDetails/ModelDetailsPage'
import { ModelStatusGuard } from '@/components/models/ModelDetails/ModelStatusGuard'
import { ModelsPage } from '@/components/models/ModelsPage'
import { SubscriptionPage } from '@/components/subscriptions/SubscriptionPage'
import { TowerHardwarePage } from '@/components/tower/TowerHardwarePage'
import { TowerPage } from '@/components/tower/TowerPage'
import { TrainingDetailsPage } from '@/components/trainings/TrainingDetailsPage'
import { VlmHardwarePage } from '@/components/vlm/VlmHardwarePage'
import { VlmPage } from '@/components/vlm/VlmPage'
import { VlmPipelinesPage } from '@/components/vlm/VlmPipelinesPage'
import { VlmResultsPage } from '@/components/vlm/VlmResultsPage'
import { AcceptWorkspaceInvitation } from '@/components/workspaces/AcceptWorkspaceInvitation'
import { WorkspaceNotificationsPage } from '@/components/workspaces/DeploymentNotifications/WorkspaceNotificationsPage'
import { WorkspaceApiKeysPage } from '@/components/workspaces/WorkspaceApiKeysPage'
import { WorkspaceProvider } from '@/components/workspaces/WorkspaceContext'
import { WorkspaceMembersPage } from '@/components/workspaces/WorkspaceMembersPage'
import { WorkspacePage } from '@/components/workspaces/WorkspacePage'
import { WorkspaceSettingsPage } from '@/components/workspaces/WorkspaceSettingsPage'
import { DEV_VLM_PIPELINES_ENABLED } from '@/config/main'
import { CanAccessRoute } from '@/permissions/CanAccessRoute'
import { ApplicationProvider } from '@/providers/ApplicationContext'
import { CrudAction, Resource } from '@/types/permissions'
import { ChangePasswordPage } from '../components/change-password/ChangePasswordPage'
import { LoginPage } from '../components/login/LoginPage'
import { ForgotPasswordPage } from '../components/password-reset/ForgotPasswordPage'
import { SetNewPasswordPage } from '../components/password-reset/SetNewPasswordPage'
import { CompleteSignUpPage } from '../components/signup/complete-registration/CompleteSignUpPage'
import { PageNotFound } from '../components/ui-shared/PageNotFound/PageNotFound'
import { PrivateRoute } from './PrivateRoute'
import { PublicAuthRoute } from './PublicAuthRoute'
import {
  AiBridgeNestedPath,
  ApplicationNestedPath,
  CameraNestedPath,
  CrossCameraNestedPath,
  DeploymentNestedPath,
  ModelNestedPath,
  RouterPath,
  TowerNestedPath,
  VlmNestedPath,
  WorkspaceNestedPath
} from './paths'

export const Routes = () => {
  return (
    <BrowserRoutes>
      <Route element={<PrivateRoute />}>
        <Route element={<WorkspaceProvider />}>
          <Route path={RouterPath.root} element={<HomeLayout />}>
            <Route element={<Navigate to={RouterPath.apps} />} index />

            <Route
              path={RouterPath.apps}
              element={
                <CanAccessRoute
                  permissions={[Resource.Applications, CrudAction.Read]}
                >
                  <PageContainer>
                    <ApplicationsPage />
                  </PageContainer>
                </CanAccessRoute>
              }
            />

            <Route
              path={RouterPath.datasets}
              element={
                <CanAccessRoute
                  permissions={[Resource.Datasets, CrudAction.Read]}
                >
                  <DatasetListPage />
                </CanAccessRoute>
              }
            />

            <Route
              path={RouterPath.datasetDetails}
              element={
                <CanAccessRoute
                  permissions={[Resource.Datasets, CrudAction.Read]}
                >
                  <DatasetDetailsPage />
                </CanAccessRoute>
              }
            />

            <Route
              path={RouterPath.devices}
              element={
                <CanAccessRoute
                  permissions={[Resource.Devices, CrudAction.Read]}
                >
                  <DeviceListPage />
                </CanAccessRoute>
              }
            />

            <Route
              path={RouterPath.deviceDetails}
              element={
                <CanAccessRoute
                  permissions={[Resource.Devices, CrudAction.Read]}
                >
                  <DeviceDetailsPage />
                </CanAccessRoute>
              }
            />

            <Route
              path={RouterPath.deviceClaim}
              element={
                <PageContainer>
                  <DeviceActivationPage />
                </PageContainer>
              }
            />

            <Route
              path={RouterPath.cameras}
              element={
                <CanAccessRoute
                  permissions={[Resource.Cameras, CrudAction.Read]}
                >
                  <CameraListPage />
                </CanAccessRoute>
              }
            >
              <Route
                path={`${CameraNestedPath.details}?`} // Using an optional (?) parameter keeps the camera details drawer always rendered. Without it, the animation won’t work.
                element={<CameraDetails />}
              />
            </Route>

            <Route
              path={RouterPath.aiBridge}
              element={
                // AIBridgeAccessPolicy gates on vlm_pipelines, not devices.
                <CanAccessRoute
                  permissions={[Resource.VlmPipelines, CrudAction.Read]}
                >
                  <AiBridgeListPage />
                </CanAccessRoute>
              }
            >
              <Route
                path={`${AiBridgeNestedPath.details}?`} // Optional (?) param keeps the details drawer mounted so the open/close animation works.
                element={<AiBridgeDetails />}
              />
            </Route>

            <Route path={RouterPath.deployments} element={<Outlet />}>
              <Route
                element={
                  <CanAccessRoute
                    permissions={[Resource.Deployments, CrudAction.Read]}
                  >
                    <DeploymentsPage />
                  </CanAccessRoute>
                }
                index
              />

              <Route
                path={DeploymentNestedPath.create}
                element={
                  <CanAccessRoute
                    permissions={[Resource.Deployments, CrudAction.Create]}
                  >
                    <ApplicationProvider>
                      <DeploymentEditorPage />
                    </ApplicationProvider>
                  </CanAccessRoute>
                }
              />
            </Route>

            <Route
              path={RouterPath.workspace}
              element={
                <CanAccessRoute
                  permissions={[Resource.Workspaces, CrudAction.Read]}
                >
                  <WorkspacePage />
                </CanAccessRoute>
              }
            >
              <Route
                element={<Navigate to={WorkspaceNestedPath.members} replace />}
                index
              />
              <Route
                path={WorkspaceNestedPath.members}
                element={<WorkspaceMembersPage />}
              />
              <Route
                path={WorkspaceNestedPath.settings}
                element={<WorkspaceSettingsPage />}
              />
              <Route
                path={WorkspaceNestedPath.apiKeys}
                element={<WorkspaceApiKeysPage />}
              />
              <Route
                path={WorkspaceNestedPath.notifications}
                element={<WorkspaceNotificationsPage />}
              />
            </Route>

            <Route
              path={RouterPath.vlm}
              element={
                <CanAccessRoute
                  permissions={[Resource.VlmPipelines, CrudAction.Read]}
                  devEnabled={DEV_VLM_PIPELINES_ENABLED}
                >
                  <VlmPage />
                </CanAccessRoute>
              }
            >
              <Route
                element={<Navigate to={VlmNestedPath.hardware} replace />}
                index
              />
              <Route
                path={VlmNestedPath.pipelines}
                element={<VlmPipelinesPage />}
              />
              <Route
                path={VlmNestedPath.hardware}
                element={<VlmHardwarePage />}
              />
              <Route
                path={VlmNestedPath.results}
                element={<VlmResultsPage />}
              />
            </Route>

            <Route
              path={RouterPath.crossCamera}
              element={
                <CanAccessRoute
                  permissions={[Resource.Applications, CrudAction.Read]}
                >
                  <CrossCameraPage />
                </CanAccessRoute>
              }
            >
              <Route
                element={
                  <Navigate to={CrossCameraNestedPath.pipelines} replace />
                }
                index
              />
              <Route
                path={CrossCameraNestedPath.installation}
                element={
                  <CanAccessRoute
                    permissions={[Resource.Devices, CrudAction.Read]}
                  >
                    <CrossCameraInstallationPage />
                  </CanAccessRoute>
                }
              />
              <Route
                path={CrossCameraNestedPath.pipelines}
                element={<CrossCameraPipelinesPage />}
              />
              <Route
                path={CrossCameraNestedPath.people}
                element={<CrossCameraPeoplePage />}
              />
            </Route>

            <Route path={RouterPath.tower} element={<TowerPage />}>
              <Route
                element={<Navigate to={TowerNestedPath.hardware} replace />}
                index
              />
              <Route
                path={TowerNestedPath.hardware}
                element={<TowerHardwarePage />}
              />
              <Route
                path={TowerNestedPath.logs}
                element={<Navigate to={TowerNestedPath.hardware} replace />}
              />
            </Route>

            <Route
              path={RouterPath.subscription}
              element={
                <PageContainer maxWidth={1200}>
                  <CanAccessRoute
                    permissions={[Resource.Subscriptions, CrudAction.Read]}
                  >
                    <SubscriptionPage />
                  </CanAccessRoute>
                </PageContainer>
              }
            />
          </Route>

          <Route path={RouterPath.appDetails} element={<AppRouteProvider />}>
            <Route element={<AppDetailsLayout />}>
              <Route
                element={<Navigate to={ApplicationNestedPath.models} replace />}
                index
              />

              <Route
                path={ApplicationNestedPath.models}
                element={
                  <CanAccessRoute
                    permissions={[Resource.MlModels, CrudAction.Read]}
                  >
                    <ModelsPage />
                  </CanAccessRoute>
                }
              />

              <Route
                path={ApplicationNestedPath.events}
                element={
                  <CanAccessRoute
                    permissions={[Resource.Events, CrudAction.Read]}
                  >
                    <PageContainer fullWidth>
                      <EventsPage />
                    </PageContainer>
                  </CanAccessRoute>
                }
              />
            </Route>

            <Route
              path={ApplicationNestedPath.modelDetails}
              element={
                <CanAccessRoute
                  permissions={[Resource.MlModels, CrudAction.Read]}
                >
                  <ModelDetailsLayout />
                </CanAccessRoute>
              }
            >
              <Route element={<ModelDetailsPage />} index />

              <Route element={<ModelStatusGuard />}>
                <Route
                  path={ModelNestedPath.selectBaseModel}
                  element={<LibraryModelListHandler />}
                />

                <Route
                  path={ModelNestedPath.selectDataset}
                  element={
                    <CanAccessRoute
                      permissions={[Resource.Datasets, CrudAction.Read]}
                    >
                      <SelectDatasetPage />
                    </CanAccessRoute>
                  }
                />

                <Route
                  path={ModelNestedPath.labelData}
                  element={
                    <CanAccessRoute
                      permissions={[Resource.Datasets, CrudAction.Read]}
                    >
                      <LabelDataPage />
                    </CanAccessRoute>
                  }
                />
              </Route>

              <Route
                path={ModelNestedPath.training}
                element={
                  <CanAccessRoute
                    permissions={[Resource.Trainings, CrudAction.Read]}
                  >
                    <TrainingDetailsPage />
                  </CanAccessRoute>
                }
              />
            </Route>
          </Route>
        </Route>

        <Route
          path={RouterPath.workspaceAcceptInvite}
          element={<AcceptWorkspaceInvitation />}
        />
      </Route>

      <Route element={<PublicAuthRoute />}>
        <Route path={RouterPath.login} element={<LoginPage />} />

        <Route
          path={RouterPath.signupComplete}
          element={<CompleteSignUpPage />}
        />

        <Route
          path={RouterPath.forgotPassword}
          element={<ForgotPasswordPage />}
        />

        <Route
          path={RouterPath.setNewPassword}
          element={<SetNewPasswordPage />}
        />
      </Route>

      <Route path={RouterPath.oauthRedirect} element={<OauthRedirect />} />

      <Route
        path={RouterPath.changePassword}
        element={<ChangePasswordPage />}
      />

      <Route path="*" element={<PageNotFound homeLink={RouterPath.root} />} />
    </BrowserRoutes>
  )
}
